// InstallableRailItem — one chip/card/row in an InstallableRail.
//
// State shape (set by parent via childrenAs:'state'):
//   display    { icon, label, tagline, color? }  — from IntegrationKind.display
//   installed  boolean                            — true when an instance exists
//   domain     string                             — 'integration'|'library'|'mcp'|…
//   kind       string                             — kind key, e.g. 'github'
//   _layout    'rail'|'grid'|'list'              — forwarded from parent

// ── Installed state badge ─────────────────────────────────────────────

export const InstallableStateBadge = {
  tag: 'span',
  padding: 'X2 Y2',
  round: 'radiusPill',
  fontSize: 'Y1',
  fontWeight: '700',
  letterSpacing: '0.04em',
  background: (el, s) => (s.installed ? 'green.1' : 'overlayStrong'),
  color: (el, s) => (s.installed ? 'green' : 'caption'),
  text: (el, s) => el.call('polyglot', s.installed ? 'apps.rail.installed' : 'apps.rail.available')
}

// ── Rail chip (horizontal scroll row) ────────────────────────────────

export const InstallableRailChip = {
  tag: 'button',
  type: 'button',
  flow: 'x',
  gap: 'Y',
  align: 'center left',
  padding: 'Y Z',
  round: 'radiusControl',
  // Fill-contrast chip (was a `line` border — border-habit sweep,
  // design-direction.md §8) — the chip's own conditional background already
  // separates it from the rail.
  background: (el, s) => (s.installed ? 'overlayStrong' : 'document'),
  cursor: 'pointer',
  flexShrink: '0',
  whiteSpace: 'nowrap',
  // A <button> centres its text by user-agent default; every variant of this
  // item pins it to the start edge (RTL-safe), so a wrapped line never centres.
  textAlign: 'start',
  transition: 'B defaultBezier',
  transitionProperty: 'background',
  // Brand language: hover is a FILL STEP on the neutral `hover` token. The
  // chip and the card washed `blue.1` while the list row stepped `hover` —
  // one family, two hover languages. Hue never marks a state in chrome.
  ':hover': {
    background: 'hover'
  },
  '@media(prefers-reduced-motion:reduce)': {
    transition: 'none'
  },

  Icon: {
    fontSize: 'B',
    name: (el, s) => s.display?.icon || s.kind || 'grid',
    color: (el, s) => s.display?.color || 'title'
  },

  Label: {
    tag: 'span',
    fontSize: 'Z2',
    fontWeight: '700',
    color: 'title',
    text: (el, s) => s.display?.label || s.kind || ''
  },

  InstallableStateBadge: {
    hide: (el, s) => !s.installed
  },

  onClick: (e, el, s) => {
    el.call('openInstallableModal', {
      domain: s.domain,
      kind: s.kind,
      installed: s.installed,
      // Forwarded so openInstallableModal can resolve no-catalogue domains
      // (mcp) straight from the tile's own self-derived state instead of
      // guessing at the marketplace's global catalog (tickets/workspace.md
      // "installed-tile manage click resolves against the wrong catalog").
      display: s.display
    })
  }
}

// ── Grid card (responsive 3–4 col grid) ──────────────────────────────

export const InstallableGridCard = {
  tag: 'button',
  type: 'button',
  flow: 'y',
  gap: 'Y',
  padding: 'Z Z2',
  round: 'radiusCard',
  // Fill-contrast card (was a `line` border — border-habit sweep,
  // design-direction.md §8) — the card's own `document` fill already
  // separates it from the grid's container.
  background: 'document',
  cursor: 'pointer',
  align: 'start left',
  // See InstallableRailChip: without this a WRAPPED tagline centres every
  // line (seen live on the "Postgres" card of the mcp grid).
  textAlign: 'start',
  transition: 'B defaultBezier',
  transitionProperty: 'background',
  ':hover': {
    background: 'hover'
  },
  '@media(prefers-reduced-motion:reduce)': {
    transition: 'none'
  },

  CardHeader: {
    flow: 'x',
    gap: 'Y',
    align: 'center space-between',
    width: '100%',

    Icon: {
      fontSize: 'C',
      name: (el, s) => s.display?.icon || s.kind || 'grid',
      color: (el, s) => s.display?.color || 'title'
    },

    InstallableStateBadge: {
      hide: (el, s) => !s.installed
    }
  },

  CardBody: {
    flow: 'y',
    gap: 'X2',
    align: 'start left',

    CardLabel: {
      tag: 'span',
      fontSize: 'Z2',
      fontWeight: '700',
      color: 'title',
      text: (el, s) => s.display?.label || s.kind || ''
    },

    CardTagline: {
      tag: 'p',
      margin: '0',
      fontSize: 'Y2',
      fontWeight: '400',
      color: 'paragraph',
      lineHeight: '1.4',
      overflow: 'hidden',
      text: (el, s) => el.call('polyglot', s.display?.tagline || s.display?.description || '')
    }
  },

  onClick: (e, el, s) => {
    el.call('openInstallableModal', {
      domain: s.domain,
      kind: s.kind,
      installed: s.installed,
      display: s.display
    })
  }
}

// ── List row (vertical management list) ──────────────────────────────

export const InstallableListRow = {
  tag: 'button',
  type: 'button',
  flow: 'x',
  gap: 'Z',
  align: 'center left',
  padding: 'Y Z2',
  // Row divider removed (was a bottom `line` border — border-habit sweep,
  // design-direction.md §8; §2.5 house rule: row separators inside rounded
  // cards stay banned — hover tint alone carries the row boundary here).
  background: 'transparent',
  cursor: 'pointer',
  width: '100%',
  textAlign: 'start',
  transition: 'B defaultBezier',
  transitionProperty: 'background',
  ':hover': {
    background: 'hover'
  },
  '@media(prefers-reduced-motion:reduce)': {
    transition: 'none'
  },

  Icon: {
    fontSize: 'B',
    name: (el, s) => s.display?.icon || s.kind || 'grid',
    color: (el, s) => s.display?.color || 'title',
    flexShrink: '0'
  },

  Info: {
    flow: 'y',
    gap: 'X2',
    flex: '1 1 0',
    minWidth: '0',

    RowLabel: {
      tag: 'span',
      fontSize: 'Z2',
      fontWeight: '700',
      color: 'title',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => s.display?.label || s.kind || ''
    },

    RowTagline: {
      tag: 'p',
      margin: '0',
      fontSize: 'Y2',
      fontWeight: '400',
      color: 'paragraph',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => el.call('polyglot', s.display?.tagline || s.display?.description || '')
    }
  },

  InstallableStateBadge: {
    flexShrink: '0'
  },

  ChevronIcon: {
    extends: 'Icon',
    name: 'arrow right',
    fontSize: 'Z2',
    color: 'caption',
    flexShrink: '0'
  },

  onClick: (e, el, s) => {
    el.call('openInstallableModal', {
      domain: s.domain,
      kind: s.kind,
      installed: s.installed,
      display: s.display
    })
  }
}

// ── Unified item — selects variant based on _layout in state ──────────
// Parent InstallableRail passes `_layout` in each child state entry.

export const InstallableRailItem = {
  InstallableRailChip: {
    hide: (el, s) => s._layout !== 'rail',
    domain: (el, s) => s.domain,
    kind: (el, s) => s.kind,
    display: (el, s) => s.display,
    installed: (el, s) => s.installed
  },

  InstallableGridCard: {
    hide: (el, s) => s._layout !== 'grid',
    domain: (el, s) => s.domain,
    kind: (el, s) => s.kind,
    display: (el, s) => s.display,
    installed: (el, s) => s.installed
  },

  InstallableListRow: {
    hide: (el, s) => s._layout !== 'list',
    domain: (el, s) => s.domain,
    kind: (el, s) => s.kind,
    display: (el, s) => s.display,
    installed: (el, s) => s.installed
  }
}
